import { EmitterType } from "hello-mono-sdk/packages/core";
import { useEffect, useState } from "react";
import ArModal from "../ArModal";
import data from "./data.json";
import infoIcon from "../../assets/images/info.png";
import reloadIcon from "../../assets/images/reload.png";
import "./ArOverlay.scss";
import Joystick from "../Joystick/Joystick";

interface IProps {
  emitter: EmitterType;
  tiltX: (angle: number) => void;
  tiltY: (angle: number) => void;
  tiltZ: (angle: number) => void;
  translateX: (angle: number) => void;
  translateY: (angle: number) => void;
  translateZ: (angle: number) => void;
  showControls: (shouldShow: boolean) => void;
  primary_color?: string;
  resetAR: () => void;
  retryAR: () => void;
  placement: "floor" | "wall" | "ceiling";
}

const INSTRUCTIONS = [
  "1. Device should be at least 4 ft above the floor.",
  "2. Device should be at least 5 ft away from the wall.",
  "3. Area/Room should be well lit.",
  "Keep the device stationary while the product is loading.",
];

const OBJECT_PLACED_MESSAGES = [
  "Move the camera to the initial view of product placement",
  "Use the white background around the product to move in the area",
  "Use pinch to zoom to increase or decrease the size of the product",
];

const AR_ADJUSTMENTS_INSTRUCTIONS = [
  "MOVE lets you adjust your model sideways, upwards and forwards.",
  "TILT lets you rotate your model in desired directions.",
];

const AR_ADJUSTMENTS_IMAGES = [
  "https://content.helloviewer.io/uploads/d9bee83e-2f2d-4730-9026-6e022f2b13a1/4_2_2024/1709541927457/Move--1-.gif",
  "https://content.helloviewer.io/uploads/2fd0a386-5e6e-4522-bc20-4e531c0bd048/4_2_2024/1709541874465/Tilt--1-.gif",
];

const ArOverlay: React.FC<IProps> = ({
  emitter,
  placement,
  resetAR,
  retryAR,
  tiltX,
  tiltY,
  tiltZ,
  translateX,
  translateY,
  translateZ,
  showControls,
  primary_color,
}) => {
  const [loadingPercent, setLoadingPercent] = useState(0);
  const [message, setMessage] = useState("");
  const [showArInfo, setShowArInfo] = useState(false);
  const [showEmptyScreen, setShowEmptyScreen] = useState(false);
  const [modalStatus, setModalStatus] = useState<
    "help" | "failed" | "success" | "none"
  >("none");
  const [modalMessages, setModalMessages] = useState<string[]>([]);
  const messages = data[placement];
  const [isObjectPlaced, setIsObjectPlaced] = useState(false);
  const [detectionTime, setDetectionTime] = useState(0);
  const [arBlackDotMessage, setArBlackDotMessage] = useState<string | null>(
    null
  );
  const [showHideControlsButton, setShowHideControlsButton] = useState(true);

  useEffect(() => {
    const detectionTimer = setInterval(() => {
      setDetectionTime((prevTime) => prevTime + 1);
    }, 1000);

    return () => {
      clearInterval(detectionTimer);
    };
  }, []);

  useEffect(() => {
    if (detectionTime >= 6 && message === data[placement].DETECTING) {
      setArBlackDotMessage(data[placement].DETECTING2nd);
    } else {
      setArBlackDotMessage(null);
    }
  }, [detectionTime, message, placement]);
  useEffect(() => {
    emitter.on("AR_MODEL_LOADING_STARTED", () => {
      setLoadingPercent(0);
    });

    emitter.on("AR_MODEL_LOADING", (data: any) => {
      if (data.progress !== 100) {
        setLoadingPercent(data.progress);
      }
    });

    emitter.on("AR_MODEL_LOADING_COMPLETED", (data: any) => {
      setLoadingPercent(99);
      setTimeout(() => {
        setLoadingPercent(100);
      }, 750);
    });

    emitter.on("AR_OBJECT_PLACED", (data: any) => {
      setMessage(messages.OBJECT_PLACED);
      setIsObjectPlaced(true);
    });

    emitter.on("AR_DETECTING_SURFACE", () => {
      setMessage(messages.DETECTING);
    });

    emitter.on("AR_SURFACE_DETECTED", () => {
      setMessage(messages.DETECTED);
    });

    emitter.on("AR_SURFACE_AVAILABLE", () => {
      setMessage(messages.SURFACE_AVAILABLE);
    });

    emitter.on("AR_SURFACE_UNAVAILABLE", () => {
      setMessage(messages.SURFACE_UNAVAILABLE);
    });

    emitter.on("AR_DETECTION_FAILED", (data: any) => {
      if (placement === "ceiling") {
        retryAR();
      } else {
        setModalMessages(data.messages);
        setModalStatus("failed");
      }
      setMessage("");
    });

    return () => {
      emitter.all.clear();
    };
  }, [emitter]);

  return (
    <div
      className="helloar-ar-container"
      style={{
        backgroundColor: loadingPercent === 100 ? "transparent" : "black",
      }}
    >
      {loadingPercent !== 100 && (
        <div className="helloar-loading-box">
          <p>{Math.floor(loadingPercent)}%</p>
        </div>
      )}
      {isObjectPlaced && showHideControlsButton && (
        <div className="helloar-hide-show-controlls-button-container">
          <button
            onClick={() => {
              setShowEmptyScreen((prev) => !prev);
            }}
          >
            {showEmptyScreen ? "Show Controls" : "Hide Controls"}
          </button>
        </div>
      )}

      {!showArInfo &&
        modalStatus === "none" &&
        isObjectPlaced &&
        !showEmptyScreen && (
          <Joystick
            tiltX={tiltX}
            tiltY={tiltY}
            tiltZ={tiltZ}
            translateX={translateX}
            translateY={translateY}
            translateZ={translateZ}
            primary_color={primary_color}
            setShowArInfo={setShowArInfo}
            showControls={showControls}
            showHideControlsButton={setShowHideControlsButton}
          />
        )}
      {!showArInfo && (
        <div className="helloar-reset-button-container">
          <button
            onClick={() => {
              setIsObjectPlaced(false);
              setModalStatus("none");
              setShowArInfo(false);
              resetAR();
            }}
          >
            <img src={reloadIcon} />
          </button>
        </div>
      )}

      {!showArInfo &&
        !showEmptyScreen &&
        message &&
        message.length > 0 &&
        modalStatus === "none" && (
          <div className="helloar-bottom-helper">
            <div className="helloar-message-container">
              <p>{arBlackDotMessage ?? message}</p>
            </div>
            <div
              className="helloar-icon-container"
              onClick={() => {
                if (isObjectPlaced) {
                  setModalStatus("help");
                  setModalMessages(INSTRUCTIONS);
                } else {
                  setModalStatus("success");
                  setModalMessages(OBJECT_PLACED_MESSAGES);
                }
              }}
            >
              <div className="helloar-img-container">
                <img src={infoIcon} />
              </div>
            </div>
          </div>
        )}

      {!showArInfo && modalStatus !== "none" && (
        <ArModal
          buttonText={modalStatus === "failed" ? "Retry" : "Done"}
          onClick={() => {
            if (modalStatus === "failed") {
              setIsObjectPlaced(false);
              retryAR();
            }
            setModalStatus("none");
          }}
          title={
            modalStatus === "help"
              ? "For The Best AR experience"
              : modalStatus === "success"
              ? "Interacting In AR"
              : "Surface Detection Failed"
          }
          instructions={modalMessages}
        />
      )}

      {showArInfo && (
        <ArModal
          buttonText={"Done"}
          onClick={() => {
            setShowArInfo(false);
            setShowHideControlsButton(true);
            showControls(true);
          }}
          title={"Adjust AR Model"}
          instructions={AR_ADJUSTMENTS_INSTRUCTIONS}
          images={AR_ADJUSTMENTS_IMAGES}
        />
      )}
    </div>
  );
};

export default ArOverlay;
